// useBaseChannels.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Channels
import { subscribeToBaseChannelReadReceipts, subscribeToBaseChannels } from 'channels/services/ChannelReadService';
import {
  createChannel,
  markChannelRead,
} from 'channels/services/ChannelWriteService';
import { toMillis } from 'channels/utils/channelTime';
import { canUserSeeChannel } from 'channels/utils/channelVisibility';

/**
 * useBaseChannels
 *
 * Provides real-time channel rows and unread state.
 *
 * @param {object} options - Hook options.
 * @param {string} options.baseKey - Active base key.
 * @param {object} options.user - Active user.
 * @param {boolean} [options.enabled=true] - When false, skip base listeners.
 *
 * @returns {object} Channels state and actions.
 */
const useBaseChannels = ({ baseKey, user, enabled = true }) => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [readReceiptsByChannel, setReadReceiptsByChannel] = useState({});

  const activeScopeKey = useMemo(() => {
    const cleanBaseKey = String(baseKey || '').trim();
    const cleanUserKey = String(user?.key || '').trim();
    return `${cleanBaseKey}:${cleanUserKey}`;
  }, [baseKey, user?.key]);

  const [loadedScopeKey, setLoadedScopeKey] = useState('');
  const hasActiveScope = Boolean(enabled && baseKey && user?.key);
  const isScopeCurrent = !hasActiveScope || loadedScopeKey === activeScopeKey;

  const visibleChannels = useMemo(() => {
    return channels.filter((channel) => canUserSeeChannel(channel, user?.key));
  }, [channels, user?.key]);

  useEffect(() => {
    if (!enabled || !baseKey || !user?.key) {
      setChannels([]);
      setReadReceiptsByChannel({});
      setLoadedScopeKey('');
      setLoading(false);
      return () => {};
    }

    setLoading(true);
    setErrorMessage('');

    const scopeKey = activeScopeKey;

    const unsubscribe = subscribeToBaseChannels(
      baseKey,
      (rows) => {
        setChannels(Array.isArray(rows) ? rows : []);
        setLoadedScopeKey(scopeKey);
        setLoading(false);
      },
      (error) => {
        setErrorMessage(error?.message || 'Failed to load channels.');
        setLoadedScopeKey(scopeKey);
        setLoading(false);
      },
      {
        viewerUserKey: user?.key,
      }
    );

    return () => unsubscribe && unsubscribe();
  }, [activeScopeKey, enabled, baseKey, user?.key]);

  // One listener covers every channel the viewer has read in this base, so adding
  // channels no longer adds listeners.
  useEffect(() => {
    if (!enabled || !baseKey || !user?.key) {
      setReadReceiptsByChannel({});
      return () => {};
    }

    // A different base or user is a different receipt set.
    setReadReceiptsByChannel({});

    const unsubscribe = subscribeToBaseChannelReadReceipts(
      baseKey,
      user.key,
      (receiptsByChannel) => {
        setReadReceiptsByChannel((prev) => {
          const next = {};

          Object.entries(receiptsByChannel).forEach(([channelKey, receipt]) => {
            next[channelKey] = receipt?.lastReadAt || null;
          });

          // Keep optimistic marks for channels the server has not echoed yet.
          Object.entries(prev).forEach(([channelKey, lastReadAt]) => {
            if (!(channelKey in next) && lastReadAt) next[channelKey] = lastReadAt;
          });

          return next;
        });
      }
    );

    return () => unsubscribe && unsubscribe();
  }, [enabled, user?.key, baseKey]);

  const resolvedChannels = useMemo(() => {
    return isScopeCurrent ? visibleChannels : [];
  }, [isScopeCurrent, visibleChannels]);

  const unreadByChannel = useMemo(() => {
    return resolvedChannels.reduce((acc, channel) => {
      const channelKey = channel?.key;
      if (!channelKey) return acc;

      const lastMessageAtMs = toMillis(channel?.lastMessageAt);
      const readAtMs = toMillis(readReceiptsByChannel[channelKey]);
      const isOwnLatestMessage = channel?.lastMessageByKey && channel.lastMessageByKey === user?.key;

      acc[channelKey] = !isOwnLatestMessage && lastMessageAtMs > readAtMs;
      return acc;
    }, {});
  }, [readReceiptsByChannel, resolvedChannels, user?.key]);

  const handleCreateChannel = useCallback(async ({ title, topic, channelType }) => {
    if (!baseKey || !user?.key) return null;

    setErrorMessage('');

    try {
      const created = await createChannel({
        baseKey,
        userKey: user.key,
        userName: user.name || user.title || user.displayName || user.email || 'Member',
        title,
        topic,
        channelType,
      });
      return created;
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to create channel.');
      return null;
    }
  }, [baseKey, user?.key, user?.name, user?.title, user?.displayName, user?.email]);

  const handleMarkRead = useCallback(async ({ channelKey, lastReadAt, lastReadMessageKey }) => {
    if (!baseKey || !channelKey || !user?.key) return;

    setReadReceiptsByChannel((prev) => ({
      ...prev,
      [channelKey]: lastReadAt || new Date(),
    }));

    try {
      await markChannelRead({
        baseKey,
        channelKey,
        userKey: user.key,
        lastReadAt: lastReadAt || undefined,
        lastReadMessageKey,
      });
    } catch (error) {
      console.error('[useBaseChannels] mark read failed:', error);
    }
  }, [baseKey, user?.key]);

  return {
    channels: resolvedChannels,
    unreadByChannel,
    loading: loading || (hasActiveScope && !isScopeCurrent),
    errorMessage,
    createChannel: handleCreateChannel,
    markRead: handleMarkRead,
  };
};

export default useBaseChannels;
